import classNames from "classnames";
import React, { useMemo } from "react";
import { IUser, IUserProgramCertificate, ProgramCertificate, ProgramData, Required } from "@interfaces/Interfaces";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { userAtom } from "@state/State";
import { userRolesSelector } from "@state/UserState";
import * as ProgramUtils from "@utils/programUtils";
import DataController from "@controllers/DataController";
import { addMonths, isAfter, isSameDay } from "date-fns";

type Props = {
  program: ProgramData;
  collapse?: boolean;
};

const ProgramCertificatesCardDisplay: React.FC<Props> = (props) => {
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);

  let userCertificatesWithExpiry: IUserProgramCertificate[] = [];
  let programCertificates: IUserProgramCertificate[] = [];

  const programRoles = allUserRoles.find((role) => role.programID === props.program.ID);

  let userRoles: string[] = [];

  if (programRoles) {
    userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
  }

  const certificatesForRole = (
    (props.program &&
      (props.program as ProgramData)?.Roles?.find((role) => role.Name === userRoles[0])?.Certificates) ??
    []
  ).filter((item) => !item.HideInDisplay);

  const mandatoryCertificates = certificatesForRole.filter((item) => item.Required === Required.Mandatory);

  const optionalCertificates = certificatesForRole.filter((item) => item.Required === Required.Optional);

  const certificatesWithExpiry: ProgramCertificate[] = certificatesForRole.filter(
    (item) => item.Duration && parseInt(item.Duration, 10) > 0
  );

  const userCertificateBelongsToProgram = (item: IUserProgramCertificate) => {
    if (item.programData.programID) {
      return item.programData.programID === props.program.ID;
    }

    return item.programData.programName === props.program.Name;
  };

  const userCertificateBelongsToRole = (item: IUserProgramCertificate) => {
    const programRole = props.program.Roles?.find((role) => role.Name === userRoles[0]);

    if (programRole) {
      return programRole.Certificates?.some((certificate) => certificate.ID === item.programData.certificateID);
    }

    return false;
  };

  const getMatchingProgramCertificate = (item: IUserProgramCertificate, certificates: ProgramCertificate[]) =>
    certificates.find(
      (certificate) =>
        certificate.ID === item.programData.certificateID || certificate.Name === item.programData.certificateType
    );

  if (user.state === "hasValue" && user.contents) {
    const allUserCertificates = user.contents.programCertificates || [];

    userCertificatesWithExpiry = allUserCertificates.filter((item) => {
      if (!userCertificateBelongsToProgram(item)) {
        return false;
      }

      if (!userCertificateBelongsToRole(item)) {
        return false;
      }

      return Boolean(getMatchingProgramCertificate(item, certificatesWithExpiry));
    });

    programCertificates = allUserCertificates.filter((item) => {
      if (!userCertificateBelongsToProgram(item)) {
        return false;
      }

      if (!userCertificateBelongsToRole(item)) {
        return false;
      }

      return Boolean(getMatchingProgramCertificate(item, certificatesForRole));
    });

    userCertificatesWithExpiry = userCertificatesWithExpiry.sort(
      (a: IUserProgramCertificate, b: IUserProgramCertificate) => {
        const certificateA = getMatchingProgramCertificate(a, certificatesWithExpiry);
        const certificateB = getMatchingProgramCertificate(b, certificatesWithExpiry);

        if (certificateA && certificateB) {
          const dateA = addMonths(new Date(a.issueDate!), parseInt(certificateA.Duration!, 10));
          const dateB = addMonths(new Date(b.issueDate!), parseInt(certificateB.Duration!, 10));

          if (isSameDay(dateA, dateB)) {
            return 0;
          } else if (isAfter(dateA, dateB)) {
            return 1;
          } else {
            return -1;
          }
        } else if (certificateA) {
          return -1;
        } else if (certificateB) {
          return 1;
        } else {
          return 0;
        }
      }
    );

    if (userCertificatesWithExpiry.length > 3) {
      userCertificatesWithExpiry = userCertificatesWithExpiry.slice(0, 3);
    }
  }

  const userMandatoryCertificates = useMemo(() => {
    return programCertificates.filter((item) => {
      return mandatoryCertificates.some((certificate) => certificate.ID === item.programData.certificateID);
    });
  }, [mandatoryCertificates, programCertificates]);

  const userOptionalCertificates = useMemo(() => {
    return programCertificates.filter((item) => {
      return optionalCertificates.some((certificate) => certificate.ID === item.programData.certificateID);
    });
  }, [optionalCertificates, programCertificates]);

  return (
    <div className="flex w-full flex-col">
      <div
        className={classNames("mb-4 mt-1 flex w-full flex-col gap-4", {
          "!mb-0": props.collapse,
        })}
      >
        {mandatoryCertificates.length > 0 && (
          <div className="flex flex-row items-end">
            <div className="text-acc-34px font-acc-700 leading-none tracking-[-0.5] text-green-light-text">
              {userMandatoryCertificates.length}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-60">
              &nbsp;{`/ ${mandatoryCertificates.length}`}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-90">
              &nbsp;{`Mandatory certificate${mandatoryCertificates.length !== 1 ? "s" : ""}`}
            </div>
          </div>
        )}
        {optionalCertificates.length > 0 && (
          <div className="flex flex-row items-end">
            <div className="text-acc-34px font-acc-700 leading-none tracking-[-0.5] text-green-light-text">
              {userOptionalCertificates.length}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-60">
              &nbsp;{`/ ${optionalCertificates.length}`}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-90">
              &nbsp;{`Optional certificate${optionalCertificates.length !== 1 ? "s" : ""}`}
            </div>
          </div>
        )}
      </div>
      {userCertificatesWithExpiry.length > 0 && (
        <div className="mb-2 flex w-full flex-col">
          <div className="mb-1 flex w-full flex-row justify-between py-2">
            <div className="text-acc-15px font-bold text-grey-90">{"Upcoming renewals"}</div>
            <div className="text-acc-13px font-semibold text-grey-60">{"Expiry date"}</div>
          </div>
          {userCertificatesWithExpiry.map((item) => {
            const certificate = getMatchingProgramCertificate(item, certificatesWithExpiry);

            return (
              <div
                key={item.programData.certificateID}
                className="w-full border-b border-grey-30 py-2 last:border-b-0"
              >
                <div className="flex flex-row items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 text-acc-15px leading-snug text-grey-90">
                    {item.programData.certificateType}
                  </div>
                  {certificate && certificate?.Duration && (
                    <div
                      className={DataController.getCertificatePillContainer({
                        certificate: item,
                        duration: certificate.Duration,
                        programID: props.program.ID,
                      })}
                    >
                      {DataController.getCertificatePillString(item, certificate.Duration)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProgramCertificatesCardDisplay;
